/**
 * Composed goal-recipe catalog entries for the restaurant industry (plan §6.3 /
 * §6.5). Filled by the restaurant P4b copywriting task — owned exclusively by it.
 * Entries omit `previewPath` (added automatically in template-catalog.ts).
 *
 * `features` / `concerns` only name things the composed seed actually
 * generates for that goal (see `../../../apps/cms/src/seeds/goal-recipes/
 * {booking,discovery,repeat,sell}.ts` and
 * `../../../apps/cms/src/seeds/adapters/restaurant.ts`) — no online payment,
 * no seat/table management, no delivery-platform integration is claimed
 * anywhere below, since none of those exist in the product today.
 */
import type { TemplateEntryDraft } from '../template-catalog';

const RESTAURANT_SUB_INDUSTRIES = [
  'レストラン',
  'カフェ',
  '居酒屋・バル',
  'ベーカリー',
  'ラーメン店',
  'テイクアウト専門店',
] as const;

export const COMPOSED_RESTAURANT_TEMPLATES: readonly TemplateEntryDraft[] = [
  {
    slug: 'restaurant-booking',
    theme: 'restaurant',
    displayName: '予約で席を埋めるお店のサイト',
    category: '飲食・カフェ',
    description:
      'ホーム上部と下部の2箇所にご予約への案内を置き、その間にメニュー抜粋・営業時間・アクセスを並べる構成。メニュー一覧・ご予約・アクセスの専用ページも備え、「見て→気になって→予約する」までを迷わせずに進めます。',
    thumb: '/lp/template-cafe.webp',
    previewFixture: 'restaurant-demo',
    fit: '和食・洋食・中華の個人店、居酒屋、コース予約を受けたいお店',
    features: [
      'ホーム上部・下部の二重ご予約導線',
      '看板メニューの抜粋＋メニュー一覧ページ',
      '営業時間・アクセス案内',
      '人数・希望日時・アレルギーを聞けるご予約フォーム',
      'アクセス専用ページ',
    ],
    goal: 'booking',
    industry: 'restaurant',
    subIndustries: [...RESTAURANT_SUB_INDUSTRIES],
    outcomeStory:
      '公開したその日から、サイトを見たお客様がそのままご予約フォームに進めるようになります。ホームの一番上に「ご予約はこちら」を置き、看板メニューや営業時間を読み終えた下にももう一度予約への入り口を用意しているので、「あとで電話しよう」と思ったまま忘れられてしまうお客様を減らせます。フォームには人数・希望日時・アレルギーの項目があらかじめ入っているので、電話で聞き取っていた内容を先に受け取った状態で当日を迎えられます。',
    concerns: [
      {
        worry: '写真がまだ用意できていない',
        answer:
          '飲食店向けのプロ撮影写真とそのまま使える初期文言があらかじめ入っています。写真は準備でき次第、差し替えていただけます。',
      },
      {
        worry: '予約の電話対応に手が回らない',
        answer:
          '人数・希望日時・アレルギーの項目まで用意したご予約フォームが最初から使える状態になっています。スパム対策と通知メールも設定済みで、システムに障害が起きてもフォームの受付自体は継続します。',
      },
      {
        worry: '開業後、更新が続けられるか不安',
        answer:
          'かんたん更新の機能で、メニューやお知らせを1分程度で追加できます。入力後はダッシュボードの「サイトを公開」ボタンを押すだけで反映されます。',
      },
    ],
    businessFlow: [
      '朝、前日に届いたご予約フォームの通知メールを確認する',
      '新しいメニューや仕入れの変化があれば、かんたん更新でメニューページに追加する',
      'ダッシュボードの「サイトを公開」ボタンを押して内容を反映する',
      '当面は電話予約と併用し、少しずつオンライン予約の比率を増やしていく',
    ],
  },
  {
    slug: 'restaurant-discovery',
    theme: 'restaurant',
    displayName: '検索と地図から見つけてもらうお店のサイト',
    category: '飲食・カフェ',
    description:
      'ホーム冒頭の「お店のこだわり」直下に営業時間・アクセスを配置し、初めて名前を知った方が「営業しているか・行けそうか」をすぐ確認できる構成。お知らせ欄も備え、検索から辿り着いた方に今も動いているお店だと伝えます。',
    thumb: '/lp/template-bakery.webp',
    previewFixture: 'bakery-demo',
    fit: 'カフェ、ベーカリー、ラーメン店など、通りがかりや検索から新しいお客様に来てほしいお店',
    features: [
      '営業時間・アクセスをファーストビュー直下に配置',
      'お店のこだわり紹介',
      '看板メニューの抜粋',
      'お知らせ欄',
      'アクセス専用ページ',
    ],
    goal: 'discovery',
    industry: 'restaurant',
    subIndustries: [...RESTAURANT_SUB_INDUSTRIES],
    outcomeStory:
      '「駅からどれくらい」「今日はやっているか」——初めて店名を検索した方が知りたいのはまずそこです。このサイトはこだわり紹介のすぐ下に営業時間とアクセスを置いているので、初訪問の方でも迷わず「行けそうだ」と判断できます。お知らせ欄も最初から用意されているので、季節のメニューや臨時休業を載せておけば、検索から来た方に「今も営業しているお店」だと伝わります。',
    concerns: [
      {
        worry: '文章を書くのが苦手',
        answer:
          'そのまま公開できる初期文言が入っています。まずは店名や住所など、お店固有の情報だけ直せば形になります。',
      },
      {
        worry: '地図アプリやSNSと連携できるか分からない',
        answer:
          '独自ドメインでの公開に対応しているので（プランによる）、Googleビジネスプロフィールなど外部の地図サービスにサイトURLとして登録していただけます。',
      },
      {
        worry: '更新を続けられるか不安',
        answer:
          'お知らせは1分で1件追加できるかんたん更新に対応しています。入力後はダッシュボードの「サイトを公開」ボタンで反映されます。',
      },
    ],
    businessFlow: [
      '開業前に住所・アクセス方法・営業時間を自分のお店の情報に書き換える',
      '開業後、季節のメニューや臨時休業をお知らせ欄にかんたん更新で追加する',
      'ダッシュボードの「サイトを公開」ボタンで反映する',
      '地図サービスやSNSのプロフィールにサイトURLを載せて、検索からの入口を増やす',
    ],
  },
  {
    slug: 'restaurant-repeat',
    theme: 'restaurant',
    displayName: '常連さんとつながり続けるお店のサイト',
    category: '飲食・カフェ',
    description:
      'ホームの上部にお知らせ欄を置き、季節メニューやイベントをすぐ伝えられる構成。お知らせ専用ページとメニュー一覧ページも備え、「また来たくなる理由」を更新し続けられます。',
    thumb: '/lp/template-izakaya.webp',
    previewFixture: 'izakaya-demo',
    fit: '常連のお客様との関係を深めたいお店、季節メニューやフェアをこまめに発信したいお店',
    features: [
      'ホーム上部のお知らせ欄',
      'お知らせ専用ページ',
      '看板メニューの抜粋＋メニュー一覧ページ',
      '季節フェアなどのお知らせ枠',
      'ご予約・ご来店の導線',
    ],
    goal: 'repeat',
    industry: 'restaurant',
    subIndustries: [...RESTAURANT_SUB_INDUSTRIES],
    outcomeStory:
      '常連のお客様は「次はいつ、何を食べに行こう」を探しに、時々サイトを覗きに来てくれます。このサイトはホームの目立つ位置にお知らせ欄を置いているので、季節メニューやフェア、営業日の変更を載せるたびに「またチェックしよう」と思ってもらえます。かんたん更新で1分あれば1件追加できるので、仕入れが変わるたびにひとこと投稿する、といった続け方もできます。',
    concerns: [
      {
        worry: '更新を続けられるか一番不安',
        answer:
          'かんたん更新の簡易フォームなら1分で1件のお知らせを追加できます。入力後はダッシュボードの「サイトを公開」ボタンを押すだけで反映されます。',
      },
      {
        worry: '何を投稿すればいいか分からない',
        answer:
          '季節フェアのお知らせ、営業日の変更、新メニューの案内など、短い一言から始められる形式です。最初の投稿例もそのまま使える文言で入っています。',
      },
      {
        worry: '常連のお客様に気づいてもらえるか不安',
        answer:
          'お知らせはホームの上部に表示され、専用ページからも一覧で振り返れます。次回のご来店時に話題にしていただきやすい導線です。',
      },
    ],
    businessFlow: [
      '仕込みや営業の合間に、季節メニューやお知らせをかんたん更新で入力する',
      'ダッシュボードの「サイトを公開」ボタンを押して反映する',
      '次回ご来店時のお客様との会話でお知らせの内容を話題にする',
      '前回の更新から日が空いていないか、ダッシュボードで確認する',
    ],
  },
  {
    slug: 'restaurant-sell',
    theme: 'restaurant',
    displayName: 'テイクアウトの注文につなげるお店のサイト',
    category: '飲食・カフェ',
    description:
      'ホーム上部に看板メニューと季節フェアの案内を並べ、そのままご注文への案内につなげる構成。メニュー一覧ページとご注文専用ページを備え、「見て→気になって→注文する」までを短くします。',
    thumb: '/lp/template-takeout.webp',
    previewFixture: 'takeout-demo',
    fit: 'テイクアウト専門店、弁当・惣菜、テイクアウトにも力を入れたい飲食店',
    features: [
      '看板メニューの抜粋＋メニュー一覧ページ',
      '季節フェアなどのお知らせ枠',
      'アクセス案内',
      'お受け取り日時・ご希望メニューを聞けるご注文フォーム',
      'ご注文専用ページ',
    ],
    goal: 'sell',
    industry: 'restaurant',
    subIndustries: [...RESTAURANT_SUB_INDUSTRIES],
    outcomeStory:
      'ホームを開くとすぐに看板メニューと季節フェアの案内が目に入り、読み進めるうちに自然とご注文への案内にたどり着きます。ご注文フォームにはお受け取り日時とご希望メニューの項目があらかじめ入っているので、電話で聞き取っていた内容を先に受け取った状態でお渡し準備ができます。アクセス案内も併記されているので、お受け取り場所を確認してからそのまま注文へ進めます。',
    concerns: [
      {
        worry: 'テイクアウトの注文をどう受け付ければいいか分からない',
        answer:
          'お受け取り日時・ご希望メニューを聞けるご注文フォームが最初から使える状態になっています。スパム対策と通知メールも設定済みです。',
      },
      {
        worry: 'オンライン決済がないと注文を受けられないのでは',
        answer:
          'このサイトはご注文内容を受け付けるフォームまでをご用意しています。お支払いは店頭など、これまでどおりの方法で受け付けられます。',
      },
      {
        worry: '写真やメニュー文章の準備に時間が割けない',
        answer:
          '飲食店向けのプロ撮影写真とそのまま使える初期文言、看板メニューの抜粋があらかじめ入っています。準備でき次第、少しずつ差し替えていただけます。',
      },
    ],
    businessFlow: [
      '朝、前日に届いたご注文フォームの通知メールを確認し、仕込み量を決める',
      '季節フェアや売り切れ情報があれば、かんたん更新でお知らせに追加する',
      'ダッシュボードの「サイトを公開」ボタンを押して内容を反映する',
      'お受け取り時間になったら、フォームの内容と照らし合わせてお渡しする',
    ],
  },
];
